import React, { Component, useEffect, useState, useMemo } from 'react';
import { useFeathers, isPermitted } from '../../app/util';
import { useSelector } from 'react-redux';
import { Link, useParams, useHistory } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  CTAButton,
  LoadingSpinner,
  BackButton,
  DataDetailToolbar,
  PreviewButton,
  DataField,
  EditOfficeField,
  EditDefaultRaceConfiguration,
  TranslationPairDownload,
  LanguageSelectButton,
  RetranslateTrigger,
  TranslationPairEditor,
  OfficeSelectInput
} from '../../components'
import styled from 'styled-components';
import moment from 'moment';
import superagent from 'superagent';
import { stateCodeForName } from '../../app/util/clean-states';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import TranslateIcon from '@mui/icons-material/Translate';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { districtTypesMatch } from './office-roll-in';

const EditTranslateIcon = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  .edit-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    font-size: 14px;
    background: white;
    border-radius: 50%;
    padding: 0px;
  }
`;

const DownloadTranslateIcon = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  .download-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    font-size: 14px;
    background: white;
    border-radius: 50%;
    padding: 0px;
  }
`;

const RetranslateIcon = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  .refresh-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    font-size: 14px;
    background: white;
    border-radius: 50%;
    padding: 0px;
  }
`;

const RaceDetail = () => {
  const feathers = useFeathers();
  const user = useSelector(state => state.user);
  const history = useHistory();
  const { key } = useParams();
  const [ districtTypeConfig, setDistrictTypeConfig ] = useState(null);

  /* handle data loading */
  const [ activeLanguage, setActiveLanguage ] = useState('en');
  const [ officeData, setOfficeData ] = useState(null);
  useEffect(() => {
    if(feathers) loadOfficeData(activeLanguage)
  }, [ feathers, activeLanguage ])

  const loadOfficeData = async (ln) => {
    try {
      const res = await feathers.getService('offices').get(key, {
        query: { ln }
      });
      console.log(res)
      setOfficeData(res);
    } catch(err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if(officeData) {
      fetchDistrictTypes()
    }
  }, [ officeData ])

  const fetchDistrictTypes = async () => {
    const state = typeof officeData?.district === 'string'
      ? await feathers.getService('districts').get(officeData.district)
      : officeData?.district;
    const stateCode = stateCodeForName(state?.name);
    if(state?.type !== 'state') return;

    if(!stateCode)  return;

    // use superagent to request 
    const response = await superagent.get('https://districts-microservice.herokuapp.com/api/v1/district-configurations/' + stateCode.toLowerCase());
    const { body } = response;
    setDistrictTypeConfig(body);
  }

  // use memo for district type, flatten for state
  const districtTypesForState = useMemo(() => {
    if(!districtTypeConfig) return;
    const { types, optionalTypes, names } = districtTypeConfig;
    const typesAll = [... new Set([ ...Object.values(types).flat(), ...(optionalTypes || []) ])];
    const typesAllFull = typesAll.map(type => {
      return {
        type,
        typeWritten: prettyNameForType(type, names)
      }
    })
    return typesAllFull
  }, [ districtTypeConfig ])

  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rollDialogOpen, setRollDialogOpen] = useState(false);
  const [rollTargetOffice, setRollTargetOffice] = useState(null);
  const [rollSubmitting, setRollSubmitting] = useState(false);
  const [rollError, setRollError] = useState(null);
  const translationPermission = isPermitted(user, [
    'super-admin',
    'editor',
    'translations',
  ]);
  const editorPermission = isPermitted(user, ['super-admin', 'editor']);
  const officeEditPermission = isPermitted(user, ['super-admin', 'editor', 'office-descriptions']);

  const handleMoreMenuClick = (event) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setMoreMenuAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const handleRollClick = () => {
    setMoreMenuAnchorEl(null);
    setRollTargetOffice(null);
    setRollError(null);
    setRollDialogOpen(true);
  };

  const closeRollDialog = () => {
    if(rollSubmitting) return;
    setRollDialogOpen(false);
    setRollTargetOffice(null);
    setRollError(null);
  };

  const rollDistrictTypesMatch = rollTargetOffice
    ? districtTypesMatch(officeData?.districtTypes, rollTargetOffice.districtTypes)
    : false;

  const handleRollConfirm = async () => {
    if(!rollTargetOffice || !rollDistrictTypesMatch || rollSubmitting) return;

    setRollSubmitting(true);
    setRollError(null);
    try {
      await feathers.getService('office-migration').create({
        sourceOffice: officeData.key,
        targetOffice: rollTargetOffice.key
      });
      setRollDialogOpen(false);
      history.push('/offices');
    } catch(err) {
      console.error('Error rolling office into another office:', err);
      setRollError(err?.message || 'Failed to start the office roll-in. Please try again.');
    } finally {
      setRollSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      // Check if there are any races associated with this office
      // Office can be stored as either the key string or the _id, so check both
      const racesWithOffice = await feathers.getService('races').find({
        query: {
          $or: [
            { office: officeData.key },
            { office: officeData._id },
            { 'office.key': officeData.key }
          ],
          $limit: 1
        }
      });

      if (racesWithOffice.total > 0) {
        alert(`Cannot delete office. There are ${racesWithOffice.total} race(s) associated with this office. Please remove or reassign those races first.`);
        setDeleteDialogOpen(false);
        return;
      }

      await feathers.getService('offices').remove(officeData._id);
      setDeleteDialogOpen(false);
      history.push('/offices');
    } catch(err) {
      console.error('Error deleting office:', err);
      alert('Failed to delete office. Please try again.');
    }
  };

  if(!officeData) {
    return (
      <Wrapper>
        <Section style={{ marginBottom: '20px' }}>
          <BackButton onClick={{ to: `/offices` }} />
        </Section>
        <EmptyStateWrapper>
          <LoadingSpinner />
        </EmptyStateWrapper>
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: `/offices` }}
        title={officeData.name}
        titleSecondary={officeData?.nameEn ? `(${officeData?.nameEn})` : ``}
        navTree={[
          { text: 'Offices', to: `/offices` },
          { text: officeData.name }
        ]}
        actionButtonsComponent={
          <ButtonContainer>
            <LanguageSelectButton
              style={{ marginRight: '4px' }}
              selected={activeLanguage}
              onSelect={setActiveLanguage}
            />
            <IconButton
              onClick={handleMoreMenuClick}
              size="large"
            >
              <MoreVertIcon />
            </IconButton>
          </ButtonContainer>
        }
      />
      <DataRow>
        <EditOfficeField
          editingDisabled={activeLanguage !== 'en' || !officeEditPermission}
          type='title'
          officeId={officeData.key}
          value={officeData.name}
          style={{ maxWidth: '400px', width: '100%' }}
          onChange={setOfficeData}
        />
      </DataRow>
      <DataRow>
        <EditOfficeField
          editingDisabled={!officeEditPermission}
          type='districtTypes'
          officeId={officeData.key}
          value={officeData.districtTypes}
          style={{ maxWidth: '400px', width: '100%' }}
          onChange={setOfficeData}
          districtTypesForState={districtTypesForState}
        />
      </DataRow>
      <Paper variant='outlined' sx={{ mt: '30px', p: 3, maxWidth: '800px' }}>
        <DataRow style={{ marginTop: 0 }}>
          <EditOfficeField
            editingDisabled={activeLanguage !== 'en' || !officeEditPermission}
            type='issuesToCover'
            officeId={officeData.key}
            value={officeData.issuesToCover}
            style={{ width: '100%' }}
            onChange={setOfficeData}
          />
        </DataRow>
        <DataRow>
          <EditOfficeField
            editingDisabled={activeLanguage !== 'en' || !officeEditPermission}
            type='short'
            officeId={officeData.key}
            value={officeData.descriptionShort}
            style={{ width: '100%' }}
            onChange={setOfficeData}
          />
        </DataRow>
        <DataRow>
          <EditOfficeField
            editingDisabled={activeLanguage !== 'en' || !officeEditPermission}
            type='long'
            officeId={officeData.key}
            value={officeData.descriptionLong}
            style={{ width: '100%' }}
            onChange={setOfficeData}
          />
        </DataRow>
        <DataRow>
          <EditOfficeField
            editingDisabled={activeLanguage !== 'en' || !officeEditPermission}
            type='impactIssues'
            officeId={officeData.key}
            value={officeData.impactIssues}
            style={{ width: '100%' }}
            onChange={setOfficeData}
          />
        </DataRow>
      </Paper>
      <EditDefaultRaceConfiguration
        officeId={officeData.key}
        value={officeData.defaultRaceConfiguration}
        onChange={setOfficeData}
        editingDisabled={!officeEditPermission}
      />
      <Menu
        id="office-more-menu"
        anchorEl={moreMenuAnchorEl}
        keepMounted
        open={Boolean(moreMenuAnchorEl)}
        onClose={handleMoreMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {translationPermission && (
          <>
            <MenuItem
              onClick={() => {
                handleMoreMenuClose();
              }}
            >
              <div style={{ width: '100%' }}>
                <TranslationPairDownload 
                  serviceName='offices' 
                  objectId={officeData?.key || officeData?._id}
                  buttonComponent={({ onClick }) => (
                    <div 
                      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                        handleMoreMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <DownloadTranslateIcon>
                          <TranslateIcon fontSize="small" />
                          <DownloadIcon className="download-icon" fontSize="small" />
                        </DownloadTranslateIcon>
                      </ListItemIcon>
                      <ListItemText>Download translations</ListItemText>
                    </div>
                  )}
                />
              </div>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMoreMenuClose();
              }}
            >
              <div style={{ width: '100%' }}>
                <TranslationPairEditor 
                  serviceName='offices' 
                  objectId={officeData?.key || officeData?._id}
                  buttonComponent={({ onClick }) => (
                    <div 
                      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                        handleMoreMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <EditTranslateIcon>
                          <TranslateIcon fontSize="small" />
                          <EditIcon className="edit-icon" fontSize="small" />
                        </EditTranslateIcon>
                      </ListItemIcon>
                      <ListItemText>Edit translations</ListItemText>
                    </div>
                  )}
                />
              </div>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMoreMenuClose();
              }}
            >
              <RetranslateTrigger 
                serviceName="offices" 
                objectId={officeData?._id}
                trigger={
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <ListItemIcon>
                      <RetranslateIcon>
                        <TranslateIcon fontSize="small" />
                        <RefreshIcon className="refresh-icon" fontSize="small" />
                      </RetranslateIcon>
                    </ListItemIcon>
                    <ListItemText>Retranslate</ListItemText>
                  </div>
                }
              />
            </MenuItem>
          </>
        )}
        {editorPermission && (
          <>
            <MenuItem onClick={handleRollClick}>
              <ListItemIcon>
                <MergeTypeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Roll office into another office</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleDeleteClick}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon sx={{ color: 'error.main' }}>
                <DeleteOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete office</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
      <Dialog
        open={rollDialogOpen}
        onClose={closeRollDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Roll this office into another office</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the destination office. Every race for "{officeData?.name}" will be reassigned,
            then this office will be permanently deleted.
          </DialogContentText>
          <OfficeSelectInput
            value={rollTargetOffice}
            onChange={event => {
              setRollTargetOffice(event.target.value);
              setRollError(null);
            }}
            restrictToOfficesWithDistrictTypes={officeData?.districtTypes}
            descendantOf={officeData?.district?._id || officeData?.district}
            excludeOfficeKeys={[officeData?.key]}
            style={{ width: '100%' }}
          />
          {rollTargetOffice && !rollDistrictTypesMatch && (
            <DialogContentText color="error" sx={{ mt: 2 }}>
              The destination office must have exactly the same district types as this office.
            </DialogContentText>
          )}
          {rollTargetOffice && rollDistrictTypesMatch && (
            <DialogContentText sx={{ mt: 2 }}>
              Confirm that "{officeData?.name}" should be rolled into "{rollTargetOffice.name}".
              This action cannot be undone.
            </DialogContentText>
          )}
          {rollError && (
            <DialogContentText color="error" sx={{ mt: 2 }}>
              {rollError}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRollDialog} disabled={rollSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleRollConfirm}
            color="error"
            variant="contained"
            disabled={!rollTargetOffice || !rollDistrictTypesMatch || rollSubmitting}
          >
            {rollSubmitting ? 'Starting…' : 'Confirm roll-in'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete this office?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This action cannot be undone. The office "{officeData?.name}" will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
}


const prettyNameForType = (type, names) => {
  const prettyName = (names || {})[type]?.prettyName;
  return prettyName || (type.split('-').map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(' ') + ' District')
}

const Wrapper = styled.div`
  width: calc(100% - 24px - 36px);
  display: flex;
  flex-direction: column;
  padding: 36px 36px 36px 24px;
  align-items: stretch;
`;

const Section = styled.div`
  margin: 0 0 48px;
  width: 100%;
  display: flex;
  h1 {
    font-size: 24px;
    margin: 0 0 0;
  }
`
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: -10px;

  div {
    margin: 0 0 0 0;
  }
`

const OfficeDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-right: 24px;
  margin-bottom: 12px;
  color: ${props => props.theme.colors.darkPurple};

  h1 {
    font-size: 28px;
    margin: 0 0 16px;
  }
`

const DataRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 30px;
`

const EmptyStateWrapper = styled.div`
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    ${({ theme }) => theme.font.normal};
    opacity: 0.8;
    font-size: 16px;
  }
`;

export default RaceDetail;
